import { useEffect, useState } from 'react';

import { Button } from 'design-system';
import { Share2 } from 'lucide-react';

import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import type { MarketApps } from '@/features/dashboard/types';
import { MarketsComponents, MarketWidgetsSkeleton, useMarketPreview } from '@/features/markets';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';

import { DashboardActionButtons } from '../../components/DashboardActionButtons';
import { DashboardLayout } from '../../components/DashboardLayout';
import { DashboardShareDialog } from '../../components/DashboardShareDialog';
import type { DashboardStep } from '../../constants/DashboardStep';
import { DASHBOARD_ACTION_BUTTON_WIDTH } from '../../constants/style';

export const ViewDashboard = ({
  setStep,
  dashboardConfig,
  tabNames,
  activeTab,
  title,
  onTabChange
}: {
  setStep: (step: DashboardStep) => void;
  dashboardConfig: MarketApps | undefined;
  tabNames: string[];
  activeTab: number;
  title: string;
  onTabChange: (index: number) => void;
}) => {
  const { data, isLoading, isFetching } = useMarketPreview(dashboardConfig);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const { setMode } = useAppForm();

  useEffect(() => {
    setMode('create');
  }, [setMode]);

  // Show loading for content area when fetching (keeps tabs visible for optimistic switching)
  const showContentLoading = isLoading || isFetching;

  return (
    <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
      <DashboardLayout
        activeTab={activeTab}
        renderRight={
          <DashboardActionButtons setStep={setStep}>
            <Button
              size='lg'
              style={{ width: DASHBOARD_ACTION_BUTTON_WIDTH }}
              variant='ghost'
              onClick={() => {
                setShareDialogOpen(true);
              }}
            >
              <Share2 aria-hidden='true' />
              Share
            </Button>
          </DashboardActionButtons>
        }
        tabNames={tabNames}
        title={title}
        onTabChange={onTabChange}
      >
        {showContentLoading ? (
          <MarketWidgetsSkeleton />
        ) : (
          data && (
            <MarketsComponents
              components={data.components}
              showAddToDashboard={false}
              showShareBlock={false}
            />
          )
        )}
      </DashboardLayout>
      <DashboardShareDialog
        open={shareDialogOpen}
        title={title}
        onClose={() => {
          setShareDialogOpen(false);
        }}
      />
    </PageInformationContext>
  );
};
